import {
    TextField,
    InputAdornment,
    List,
    ListItem,
    Divider,
    IconButton,
    Box,
    Typography,
    Button,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress
} from '@mui/material';
import { isEqual } from 'lodash';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import styles from '../../styles/primaryList.module.scss';
import { closeViewDialog, openViewDialog, saveView, setDefaultFilteredCostCenterList, setDefaultFilteredSiteList, setFilteredCostCenterList, setFilteredSiteList } from 'modules/cbre-flow-view-management/ducks/actions';
import { useDispatch, useSelector } from 'react-redux';
import { DIALOG_SUBTYPES, DIALOG_TYPES, SYSTEM_DEFAULT_VIEW_NAME, SYSTEM_DEFAULT_VIEW_TYPE } from 'modules/cbre-flow-view-management/ducks/constants';
import { ViewManagementAddFiltersButton } from '../add-filters-management';
import { getAllFilteredLists, getCostCenterList, getCurrentView, getCurrentViewAllDefaultFilteredLists, getSaveLoading, getSiteList, getUpdateViewAction, getViewManagementListApiUrl, getViews, getViewsInfo, hasModifiedFiltersSelector } from 'modules/cbre-flow-view-management/ducks/selectors';
import { DeleteOutline, Info, RemoveCircleOutline, Search } from '@mui/icons-material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useEffect, useRef, useState } from 'react';
import SaveViewAsContent from './SaveViewAsContent';
import IntlMessages from 'util/IntlMessages';
import SearchBox from 'components/SearchBox';
import RenderInput from 'components/renderInput';
import { useForm } from 'react-hook-form';
import { ReactComponent as CubeIcon } from 'assets/images/icons/cube.svg';
import apiService from 'util/apiService';

interface ListItemData {
    title: string;
    description: string;
    navigateTo: string;
    filteredList?: any[];
}

interface PrimaryListProps {
    selectedClient: any;
    setSelectedClient: (client: any) => void;
}

const PrimaryList: React.FC<PrimaryListProps> = ({ selectedClient, setSelectedClient }) => {

    const costCenterList = useSelector(getCostCenterList);
    const siteList = useSelector(getSiteList);
    const currentView = useSelector(getCurrentView);
    const saveLoading = useSelector(getSaveLoading);
    const saveAsAnchorEl = useRef(null);
    const isFilterModified = useSelector(hasModifiedFiltersSelector);
    const [saveViewOpen, setSaveViewOpen] = useState(false);
    const [isRequestAccessLoading, setIsRequestAccessLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [searchResults, setSearchResults] = useState<{ sites: any[], costCenters: any[] }>({ sites: [], costCenters: [] });
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [searchLoading, setSearchLoading] = useState<boolean>(false);
    const [expandedAccordions, setExpandedAccordions] = useState<Set<string>>(new Set(['sites', 'costCenters']));
    const [hoveredItem, setHoveredItem] = useState<string | null>(null);
    const fetchViewListUrl = useSelector(getViewManagementListApiUrl);
    const currentViewInfo = useSelector(getViewsInfo);
    const saveViewApiUrl = useSelector(getUpdateViewAction(currentViewInfo?.portfolioViewId || ''));
    const methods = useForm({
        defaultValues: {
            search: '',
        }
    })
    const searchValue = methods.watch('search');

    const allFilteredLists = useSelector(getAllFilteredLists)
    const allDefaultFilteredLists = useSelector(getCurrentViewAllDefaultFilteredLists);
    const views = useSelector(getViews);

    console.log('allFilteredLists', allFilteredLists);

    const handleSaveViewClose = () => {
        setSaveViewOpen(false);
    }

    const handleClearSearch = () => {
        methods.setValue('search', '');
    };

    useEffect(() => {
        // Debounce search
        const delayDebounceFn = setTimeout(() => {
            if (searchValue?.trim()) {
                handleSearch(searchValue);
            } else {
                setIsSearching(false);
                setSearchResults({ sites: [], costCenters: [] });
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchValue]);

    const handleSearch = async (keyword: string) => {
        if (!currentViewInfo?.clientCode) return;

        setSearchLoading(true);
        setIsSearching(true);
        try {
            const response = await apiService.get(`${fetchViewListUrl}clientCode=${currentViewInfo.clientCode}&searchFrom=GLOBAL&search=${encodeURIComponent(keyword)}`);
            if (response.statusCode === 200) {
                const accessSites = response.data?.accessSites?.data || [];
                const costCenters = response.data?.allCostCenters?.data || [];

                const sitesWithAccess = accessSites.filter((item: any) =>
                    item.accessStatus === 'GRANTED' && item.accessLevel !== "NO_ACCESS"
                );

                const selectedSiteIds = new Set(allFilteredLists.sites.map((s: any) => s.site));
                const selectedSiteNames = new Set(allFilteredLists.sites.map((s: any) => s.siteName).filter(Boolean));

                const filteredCostCenters = selectedSiteIds.size === 0
                    ? costCenters
                    : costCenters.filter((cc: any) =>
                        selectedSiteIds.has(cc.site) || selectedSiteNames.has(cc.siteName)
                    );

                setSearchResults({
                    sites: sitesWithAccess,
                    costCenters: filteredCostCenters
                });
            } else {
                setSearchResults({ sites: [], costCenters: [] });
            }
        } catch (error) {
            console.error('Search error:', error);
            setSearchResults({ sites: [], costCenters: [] });
        } finally {
            setSearchLoading(false);
        }
    };

    const handleAccordionToggle = (accordionId: string) => {
        const newExpanded = new Set(expandedAccordions);
        if (expandedAccordions.has(accordionId)) {
            newExpanded.delete(accordionId);
        } else {
            newExpanded.add(accordionId);
        }
        setExpandedAccordions(newExpanded);
    };

    const handleToggleSite = (site: any) => {
        const isAdded = allFilteredLists.sites.some((s: any) => s.site === site.site);
        if (isAdded) {
            const updated = allFilteredLists.sites.filter((s: any) => s.site !== site.site);
            dispatch(setFilteredSiteList(currentView, updated));
            
            // Also remove cost centers belonging to the deleted site
            const deletedSiteIdentifiers = [
                site.site,
                site.siteName,
                site.siteId
            ].filter(Boolean).map(String);
            
            const updatedCostCenters = allFilteredLists.costCenters.filter((cc: any) => {
                const ccSiteIdentifiers = [
                    cc.site,
                    cc.siteName,
                    cc.siteId
                ].filter(Boolean).map(String);
                
                // Keep cost center only if it doesn't match the deleted site
                return !ccSiteIdentifiers.some(ccId => 
                    deletedSiteIdentifiers.includes(ccId)
                );
            });
            dispatch(setFilteredCostCenterList(currentView, updatedCostCenters));
        } else {
            dispatch(setFilteredSiteList(currentView, [...allFilteredLists.sites, site]));
        }
    };

    const handleToggleCostCenter = (cc: any) => {
        const isAdded = allFilteredLists.costCenters.some((c: any) => c.costCenterId === cc.costCenterId);
        if (isAdded) {
            const updated = allFilteredLists.costCenters.filter((c: any) => c.costCenterId !== cc.costCenterId);
            dispatch(setFilteredCostCenterList(currentView, updated));
        } else {
            dispatch(setFilteredCostCenterList(currentView, [...allFilteredLists.costCenters, cc]));
        }
    };

    const getCostCenterTitle = (costCenter: any) => {
        const displayName = typeof costCenter?.displayName === 'string' ? costCenter.displayName.trim() : '';
        const name = typeof costCenter?.name === 'string' ? costCenter.name.trim() : '';
        const description = typeof costCenter?.description === 'string' ? costCenter.description.trim() : '';

        if (displayName) {
            return displayName;
        }

        if (name && description) {
            return `${name} - ${description}`;
        }

        return name || description || '';
    };


    const sitesWithAccess = siteList?.filter((item: any) => item.accessStatus === 'GRANTED' && item.accessLevel !== "NO_ACCESS") || [];

    const listItems: ListItemData[] = [
        { title: `All sites with access (${sitesWithAccess.length})`, description: 'Site', navigateTo: DIALOG_SUBTYPES.EDIT_SITE_LIST, filteredList: allFilteredLists.sites },
        { title: `All cost centers (${costCenterList.length})`, description: 'Cost Center', navigateTo: DIALOG_SUBTYPES.EDIT_COST_CENTER_LIST, filteredList: allFilteredLists.costCenters },
    ];

    const hasAnyFilters = (allFilteredLists.sites.length > 0) || (allFilteredLists.costCenters.length > 0);

    const dispatch = useDispatch();
    const handleClose = () => {
        dispatch(closeViewDialog());
    };

    const getCurrentViewData = () => {
        return views.find((view: any) => view.viewName === currentView);
    };

    const currentViewData = getCurrentViewData();
    const isSystemDefaultView = currentViewData?.viewType === SYSTEM_DEFAULT_VIEW_TYPE;


    const handleSaveAndApply = async () => {
        if (!isSystemDefaultView && currentViewInfo?.portfolioViewId) {
            const viewData = {
                account: currentViewInfo.clientCode,
                filterSelections: {
                    sites: allFilteredLists.sites,
                    costCenters: allFilteredLists.costCenters,
                },
            };
            await dispatch(saveView({ apiUrl: `${saveViewApiUrl}clientCode=${currentViewInfo.clientCode}`, viewData, mode: "update" }));
        }

        dispatch(setDefaultFilteredSiteList(currentView, allFilteredLists.sites));
        dispatch(setDefaultFilteredCostCenterList(currentView, allFilteredLists.costCenters));

        dispatch(setFilteredSiteList(currentView, allFilteredLists.sites));
        dispatch(setFilteredCostCenterList(currentView, allFilteredLists.costCenters));
        handleClose();
    }

    const RenderBanner = () => {
        if (isFilterModified) {
            return (
                <div className="py-2 banner-container">
                    <Alert
                        severity="info"
                        icon={<Info style={{ color: '#8585E0' }} />}
                        style={{ background: '#E7E7F9', border: '1px solid #8585E0', alignItems: 'center' }}

                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            This view has unsaved changes. You can save it, apply it, or undo changes.
                            <Button
                                variant="outlined"
                                color="primary"
                                style={{ textTransform: 'none', whiteSpace: 'nowrap', marginLeft: '12px', fontWeight: 600 }}
                                onClick={() => {
                                    // Reset to default filtered lists
                                    dispatch(setFilteredSiteList(currentView, allDefaultFilteredLists.sites));
                                    dispatch(setFilteredCostCenterList(currentView, allDefaultFilteredLists.costCenters));
                                }}
                            >
                                Undo Changes
                            </Button>
                        </div>
                    </Alert>
                </div>
            );
        }
        return null;
    };



    return (
        <Box className={styles.container}>
            <div style={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <Box className={styles.viewHeader}>
                    <Box className={styles.titleRow}>
                        <Typography className={styles.title}>
                            {!isSystemDefaultView && <CubeIcon style={{ marginRight: '8px' }} />}
                            {currentView}{
                                isFilterModified && ' *'}
                        </Typography>
                        {hasAnyFilters && <ViewManagementAddFiltersButton buttonProps={{ width: "100%", color: "light", style: { width: "100%" } }} />}

                    </Box>
                    <Box className={styles.breadcrumb}>
                        <Typography className={styles.breadcrumbText}>{currentViewInfo?.clientName}</Typography>
                        <ChevronRightIcon className={styles.breadcrumbIcon} />
                    </Box>
                </Box>

                <Box className={styles.body}>
                    {RenderInput({
                        type: 'text',
                        label: 'Search',
                        errorMessages: [],
                        name: 'search',
                        methods: methods,
                        InputProps: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    {searchLoading ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        <>
                                            {searchValue && (
                                                <IconButton
                                                    onClick={handleClearSearch}
                                                    edge="end"
                                                    sx={{ cursor: 'pointer', padding: '4px', marginRight: '8px' }}
                                                >
                                                    <CloseIcon />
                                                </IconButton>
                                            )}
                                            <SearchIcon />
                                        </>
                                    )}
                                </InputAdornment>
                            ),
                        },
                        className: styles.searchBox,
                        module: ''
                    })}

                    {isSearching && (
                        <Typography variant="body2" className="mb-2" style={{ color: '#666' }}>
                            {searchResults.sites.length + searchResults.costCenters.length} search results
                        </Typography>
                    )}

                    {isSearching ? (
                        <Box className={styles.accordionContainer}>
                            {/* Sites Accordion */}
                            <Accordion
                                expanded={expandedAccordions.has('sites')}
                                onChange={() => handleAccordionToggle('sites')}
                                className={styles.accordion}
                                sx={{ marginBottom: 0, '&.Mui-expanded': { marginBottom: 0 } }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={styles.accordionSummary}>
                                    <Typography className={styles.accordionTitle}>
                                        Sites ({searchResults.sites.length})
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails className={styles.accordionDetails}>
                                    <List className={styles.list}>
                                        {searchResults.sites.length === 0 ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 20, gap: 1 }}>
                                                <Typography variant="body2" color="textSecondary"> {searchLoading ? "Loading..." : "No results"}</Typography>
                                            </Box>
                                        ) : (
                                            searchResults.sites.map((site: any, index: number) => {
                                                const isAdded = allFilteredLists.sites.some((s: any) => s.site === site.site);
                                                const isHovered = hoveredItem === `site-${site.site}`;

                                                return (
                                                    <Box key={site.site}>
                                                        <ListItem
                                                            className={styles.listItem}
                                                            onMouseEnter={() => setHoveredItem(`site-${site.site}`)}
                                                            onMouseLeave={() => setHoveredItem(null)}
                                                            onClick={() => handleToggleSite(site)}
                                                            sx={{ cursor: 'pointer' }}
                                                        >
                                                            <Box className={styles.itemContent}>
                                                                <Box className={styles.textContainer}>
                                                                    <Typography className={styles.itemTitle}>{site.siteName}</Typography>
                                                                </Box>
                                                            </Box>
                                                            {isAdded ? (
                                                                <RemoveCircleOutlineIcon className={styles.editIcon} />
                                                            ) : (
                                                                isHovered && <AddCircleIcon className={styles.editIcon} />
                                                            )}
                                                        </ListItem>
                                                        {index < searchResults.sites.length - 1 && <Divider className={styles.divider} />}
                                                    </Box>
                                                );
                                            })
                                        )}
                                    </List>
                                </AccordionDetails>
                            </Accordion>

                            {/* Cost Centers Accordion */}
                            <Accordion
                                expanded={expandedAccordions.has('costCenters')}
                                onChange={() => handleAccordionToggle('costCenters')}
                                className={styles.accordion}
                                sx={{ marginTop: 0, '&.Mui-expanded': { marginTop: 0 } }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={styles.accordionSummary}>
                                    <Typography className={styles.accordionTitle}>
                                        Cost centers ({searchResults.costCenters.length})
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails className={styles.accordionDetails}>
                                    <List className={styles.list}>
                                        {searchResults.costCenters.length === 0 ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 20, gap: 1 }}>
                                                <Typography variant="body2" color="textSecondary"> {searchLoading ? "Loading..." : "No results"}</Typography>
                                            </Box>
                                        ) : (
                                            searchResults.costCenters.map((cc: any, index: number) => {
                                                const isAdded = allFilteredLists.costCenters.some((c: any) => c.costCenterId === cc.costCenterId);
                                                const isHovered = hoveredItem === `cc-${cc.costCenterId}`;
                                                const costCenterSiteText = cc.siteName || cc.site || 'Cost center';
                                                const costCenterTitle = getCostCenterTitle(cc);

                                                return (
                                                    <Box key={cc.costCenterId}>
                                                        <ListItem
                                                            className={styles.listItem}
                                                            onMouseEnter={() => setHoveredItem(`cc-${cc.costCenterId}`)}
                                                            onMouseLeave={() => setHoveredItem(null)}
                                                            onClick={() => handleToggleCostCenter(cc)}
                                                            sx={{ cursor: 'pointer' }}
                                                        >
                                                            <Box className={styles.itemContent}>
                                                                <Box className={styles.textContainer}>
                                                                    <Typography className={styles.itemTitle}>{costCenterTitle}</Typography>
                                                                    <Typography className={styles.itemDescription}>{costCenterSiteText}</Typography>
                                                                </Box>
                                                            </Box>
                                                            {isAdded ? (
                                                                <RemoveCircleOutlineIcon className={styles.editIcon} />
                                                            ) : (
                                                                isHovered && <AddCircleIcon className={styles.editIcon} />
                                                            )}
                                                        </ListItem>
                                                        {index < searchResults.costCenters.length - 1 && <Divider className={styles.divider} />}
                                                    </Box>
                                                );
                                            })
                                        )}
                                    </List>
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                    ) : (
                        <List className={styles.list}>
                            {listItems.map((item, index) => (
                                <Box key={index} sx={{ pt: index === 0 ? 0 : 1, pb: index === listItems.length - 1 ? 0 : 1 }}>
                                    <Typography
                                        className={styles.itemDescription}
                                        sx={{ textTransform: 'uppercase' }}
                                    >
                                        {item.description === 'Site'
                                            ? 'Sites'
                                            : 'Cost Centers'}
                                    </Typography>
                                    {!item.filteredList || item.filteredList.length === 0 ? <ListItem className={styles.listItem}>
                                        <Box className={styles.itemContent}>
                                            <Box className={styles.textContainer}>
                                                <Typography className={styles.itemTitle}>
                                                    {item.title}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <IconButton className={styles.editButton} aria-label="edit" onClick={() => { dispatch(openViewDialog(DIALOG_TYPES.EDIT, item.navigateTo as any)) }}>
                                            <EditIcon className={styles.editIcon} />
                                        </IconButton>
                                    </ListItem> : <>
                                        {item.filteredList.map((filteredItem: any, filteredIndex: number) => {

                                                const key = item.description === 'Cost Center' ? filteredItem.description :
                                                        filteredItem.site;
                                                const title = item.description === 'Cost Center' ? getCostCenterTitle(filteredItem) :
                                                        filteredItem.siteName;

                                                const description = item.description === 'Cost Center'
                                                    ? (filteredItem.siteName || filteredItem.site || '')
                                                    : 'Site';

                                                return (
                                                <Box key={filteredItem[key]} sx={{ pr: 1 }}>
                                                    <ListItem
                                                        className={styles.listItem}
                                                    >
                                                            <Box className={styles.itemContent}>
                                                                <Box className={styles.textContainer}>
                                                                    <Typography className={styles.itemTitle}>
                                                                        {title}
                                                                    </Typography>
                                                                    <Typography className={styles.itemDescription}>
                                                                        {description}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        <IconButton aria-label="delete"
                                                            onClick={() => {
                                                                // Determine the type and remove the appropriate item
                                                                if (item.description === 'Cost Center') {
                                                                    const updatedCostCenters = allFilteredLists.costCenters.filter(
                                                                        (cc: any) => cc.costCenterId !== filteredItem.costCenterId
                                                                    );
                                                                    dispatch(setFilteredCostCenterList(currentView, updatedCostCenters));
                                                                } else {
                                                                    const updatedSites = allFilteredLists.sites.filter(
                                                                        (site: any) => site.site !== filteredItem.site
                                                                    );
                                                                    dispatch(setFilteredSiteList(currentView, updatedSites));
                                                                    
                                                                    // Also remove cost centers belonging to the deleted site
                                                                    const deletedSiteIdentifiers = [
                                                                        filteredItem.site,
                                                                        filteredItem.siteName,
                                                                        filteredItem.siteId
                                                                    ].filter(Boolean).map(String);
                                                                    
                                                                    const updatedCostCenters = allFilteredLists.costCenters.filter((cc: any) => {
                                                                        const ccSiteIdentifiers = [
                                                                            cc.site,
                                                                            cc.siteName,
                                                                            cc.siteId
                                                                        ].filter(Boolean).map(String);
                                                                        
                                                                        // Keep cost center only if it doesn't match the deleted site
                                                                        return !ccSiteIdentifiers.some(ccId => 
                                                                            deletedSiteIdentifiers.includes(ccId)
                                                                        );
                                                                    });
                                                                    dispatch(setFilteredCostCenterList(currentView, updatedCostCenters));
                                                                }

                                                            }}
                                                        >
                                                            <DeleteOutline className={styles.editIcon} />
                                                        </IconButton>
                                                    </ListItem>
                                                </Box>
                                            );
                                        })}
                                    </>}
                                    {index < listItems.length - 1 && (
                                        <Divider className={styles.divider} sx={{ my: 2 }} />
                                    )}
                                </Box>
                            ))}
                        </List>
                    )}
                </Box>

            </div>
            {<RenderBanner />}
            <Box className={styles.footer}>
                <Button ref={saveAsAnchorEl} variant="contained" color="light" style={{ textTransform: 'none' }} sx={{ boxShadow: "none", '&:hover': { boxShadow: "none" } }}
                    onClick={() => setSaveViewOpen(true)}
                >
                    Save as
                </Button>
                <SaveViewAsContent anchorEl={saveAsAnchorEl.current} isPopover={true} open={saveViewOpen} onClose={handleSaveViewClose} selectedClient={selectedClient} />

                <div>
                    <Button variant="outlined" color="primary" onClick={() => {
                        dispatch(setFilteredSiteList(currentView, allDefaultFilteredLists.sites));
                        dispatch(setFilteredCostCenterList(currentView, allDefaultFilteredLists.costCenters));
                        handleClose()
                    }} sx={{ mr: "12px" }}   >
                        Cancel
                    </Button>
                    <Button variant="contained" color="primary" disabled={!isFilterModified || saveLoading} style={{ textTransform: 'none' }} onClick={handleSaveAndApply} >
                        {saveLoading && <CircularProgress size={24} />}
                        {isSystemDefaultView ? "Apply" : "Save & apply"}
                    </Button>
                </div>
            </Box>
        </Box>
    );
};

export default PrimaryList;
